import React from "react";
import { Link } from "react-router-dom";
import { Mail, ExternalLink, Facebook, Instagram, Youtube, Music2 } from "lucide-react";
import { useSite } from "../context/AppContext";

export default function Footer() {
  const { site } = useSite();
  const nav = site?.navigation?.items?.filter((i) => i.visible) || [];
  const contact = site?.contact || {};
  const fee = site?.settings?.consultationFee;
  const social = site?.branding?.socialLinks || {};
  const logo = site?.branding?.logoUrl;

  return (
    <footer className="bg-[#061325] text-slate-300 border-t border-amber-500/20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-3">
        <div>
          <div className="flex flex-col items-start gap-4 mb-4">
            {logo && <img src={logo} alt="LD Travel logo" className="h-28 w-28 rounded-full object-cover ring-2 ring-amber-400/50 shadow-[0_10px_40px_rgba(212,175,55,0.25)]" data-testid="footer-logo" />}
            <div>
              <p className="font-heading text-2xl text-amber-100 leading-none">LD Travel</p>
              <p className="text-[11px] tracking-[0.3em] text-amber-400/80 mt-1 uppercase">Dream · Explore · Book · Repeat</p>
            </div>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
            Cruises and group trips planned with care by your dedicated travel advisor.
          </p>
          <div className="flex gap-3 mt-5">
            {social.facebook && <a href={social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook (opens in a new tab)" data-testid="social-facebook" className="p-2 rounded-full border border-amber-500/25 hover:bg-white/5 transition-colors"><Facebook className="w-4 h-4 text-amber-200" /></a>}
            {social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram (opens in a new tab)" data-testid="social-instagram" className="p-2 rounded-full border border-amber-500/25 hover:bg-white/5 transition-colors"><Instagram className="w-4 h-4 text-amber-200" /></a>}
            {social.tiktok && <a href={social.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok (opens in a new tab)" data-testid="social-tiktok" className="p-2 rounded-full border border-amber-500/25 hover:bg-white/5 transition-colors"><Music2 className="w-4 h-4 text-amber-200" /></a>}
            {social.youtube && <a href={social.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube (opens in a new tab)" data-testid="social-youtube" className="p-2 rounded-full border border-amber-500/25 hover:bg-white/5 transition-colors"><Youtube className="w-4 h-4 text-amber-200" /></a>}
          </div>
        </div>

        <nav aria-label="Footer navigation">
          <p className="eyebrow mb-4">Explore</p>
          <ul className="space-y-2.5">
            {nav.map((item) => (
              <li key={item.id}>
                {item.external ? (
                  <a href={item.path} {...(item.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})} data-testid={`footer-link-${item.id}`} className="text-sm text-amber-200 hover:text-amber-100 transition-colors inline-flex items-center gap-1.5">
                    {item.label} <ExternalLink className="w-3 h-3" aria-hidden="true" />
                    {item.newTab && <span className="sr-only">(opens Facebook in a new tab)</span>}
                  </a>
                ) : (
                  <Link to={item.path} data-testid={`footer-link-${item.id}`} className="text-sm text-slate-300 hover:text-amber-200 transition-colors">
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
            <li>
              <Link to="/privacy" data-testid="footer-link-privacy" className="text-sm text-slate-300 hover:text-amber-200 transition-colors">
                Privacy Notice
              </Link>
            </li>
          </ul>
        </nav>

        <div>
          <p className="eyebrow mb-4">Contact</p>
          <ul className="space-y-2.5 text-sm">
            <li data-testid="footer-advisor-name">{contact.title || "Travel Advisor"}: {contact.advisorName}</li>
            {contact.registration && <li data-testid="footer-registration">Florida registration number: {contact.registration}</li>}
            {contact.email && (
              <li>
                <a href={`mailto:${contact.email}`} data-testid="footer-email-link" className="inline-flex items-center gap-2 text-amber-200 hover:text-amber-100 transition-colors break-all">
                  <Mail className="w-4 h-4 shrink-0" aria-hidden="true" /> {contact.email}
                </a>
              </li>
            )}
            {contact.phone && <li data-testid="footer-phone">{contact.phone}</li>}
            {contact.address && <li data-testid="footer-address" className="whitespace-pre-line">{contact.address}</li>}
          </ul>
          <a
            href="https://www.evotravelagent.com/ldixontravel"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-travel-website-link"
            className="inline-flex items-center gap-2 mt-5 text-sm text-amber-200 border border-amber-500/30 rounded-full px-4 py-2 hover:bg-white/5 transition-colors"
          >
            Visit My Travel Website <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        </div>
      </div>

      <div className="border-t border-amber-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} LD Travel · Lisa M Dixon, Travel Advisor</p>
          {fee && <p data-testid="footer-fee-notice" className="text-amber-300/90 font-medium text-[13px]">{fee}</p>}
          <Link to="/admin" data-testid="footer-admin-link" className="hover:text-amber-200 transition-colors">Owner Login</Link>
        </div>
      </div>
    </footer>
  );
}
